import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, message } from 'antd';
import { ContentState, EditorState } from 'draft-js';
import shallow from 'zustand/shallow';
import { useStore } from '../../store/store';
import BoxDraftText from '../common/BoxDraftText';
import BoxActionsPanel from '../common/BoxActionsPanel';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import InfoCardFields from '../common/boxStyleFields/InfoCardFields';
import BoxStyleTemplatePicker from '../common/boxStyleFields/BoxStyleTemplatePicker';
import { useBoxPropsEditor } from '../common/boxStyleFields/useBoxPropsEditor';
import { useCardBank } from '../common/boxStyleFields/useCardBank';
import LabelledRow from '../common/LabelledRow';
import DuotoneLightIcon from '../common/DuotoneLightIcon';
import { NavbarButton } from '../Layout/NavbarButton';
import { decorator } from '../RichEditor/utils';
import { postCardBankItem } from '../../requests/documentDatas';
import { iconButtonStyle, mergeConfigurationInfoCardStyles } from '../../utils/styles';
import { boxPropsFromStyle } from '../../shared/boxes/styleTemplate';
import {
  infoCardBankProps,
  infoCardPresetTemplates,
  infoCardSampleContentPatch,
} from '../../shared/boxes/infoCard/infoCardStyles';
import {
  INFO_CARD_DESCRIPTION_STYLE,
  infoCardTextAlign,
  infoCardTitleTypography,
} from '../../shared/boxes/infoCard/infoCardRender';
import InfoCardFace from './InfoCardFace';
import InfoCardPreview from '../RightSide/configurationModal/InfoCardPreview';
import { sectionAccentColor } from '../../shared/ui/editorAccents';
import { panelGroupLabelStyle } from '../../shared/ui/panelHeading';

/**
 * Box « Carte info » du document — la jumelle du bloc web `InfoCard`.
 *
 * La box porte SES valeurs (`content.card`) : un style du template n'est qu'un
 * **modèle**, recopié ici quand on l'applique, et tout se personnalise ensuite
 * dans le panel de droite. Les deux textes vivent à côté, en Draft.js.
 */
const selector = (box) => ({
  updateBox, selectedBoxId, configuration, tmp, currentDocument, currentProposal,
}) => ({
  updateBox,
  isSelected: selectedBoxId === box.id,
  configuration,
  tmp,
  currentDocument,
  currentProposal,
});

/** Le texte nu d'un éditeur Draft, pour le comparer aux exemples des modèles. */
const plainText = (editorState) => editorState?.getCurrentContent().getPlainText() ?? '';

const textEditorState = (text) => EditorState.createWithContent(
  ContentState.createFromText(text),
  decorator,
);

/** La mise en forme du texte en premier, les réglages de la carte ensuite. */
const TEXT_PANEL_ORDER = 0;

/**
 * Les modèles tout faits de la galerie du site, proposés à côté de ceux du
 * template : le configurateur n'en contient souvent qu'un, et un témoignage ou
 * un chiffre clé se pose sans passer par lui.
 */
const PRESET_TEMPLATES = infoCardPresetTemplates();

// Sous-groupe de la section, tracé comme ceux du tableau (NavbarTable) et comme
// les groupes de réglages du panneau (cf. FieldGroupLabel).
const groupLabelStyle = (label) => panelGroupLabelStyle(sectionAccentColor(label));

/**
 * La vignette d'un modèle : la carte en réduction, avec son texte d'exemple —
 * et les couleurs de ses textes, sinon un titre vert enregistré paraît noir.
 */
const renderTemplatePreview = (template) => <InfoCardPreview style={template} />;

const InfoCardBox = ({ box }) => {
  const { t } = useTranslation();
  // `shallow` : le sélecteur forge un objet à chaque appel, la carte et toutes
  // les vignettes du panel se redessinaient à chaque changement du store.
  const {
    updateBox, isSelected, configuration, tmp, currentDocument, currentProposal,
  } = useStore(selector(box), shallow);
  const templates = useMemo(
    () => mergeConfigurationInfoCardStyles(configuration, tmp),
    [configuration, tmp],
  );
  const bankTemplates = useCardBank(isSelected);
  const [bankStyleName, setBankStyleName] = useState('');
  const [savingToBank, setSavingToBank] = useState(false);
  const {
    props: style, setProp, labelOfColor, colorOfLabel,
  } = useBoxPropsEditor(box, 'card');

  // Les barres de mise en forme suivent le dernier texte à avoir pris le focus.
  const [focusedKey, setFocusedKey] = useState('title');
  const titleTextRef = useRef(null);
  const descriptionTextRef = useRef(null);

  // Draft aligne chaque bloc lui-même : le `textAlign` de la carte ne le
  // traverse pas, il faut le lui redonner.
  const textAlign = infoCardTextAlign(style.contentAlign);

  /**
   * `nextProps` reçoit les valeurs courantes : un modèle du configurateur les
   * remplace toutes, un modèle de la galerie ne pose que ses props de style —
   * il n'en porte pas d'autres, et écraser le sac entier effacerait l'icône,
   * la photo ou les couleurs de la carte.
   */
  const applyTemplate = (template, nextProps) => {
    // La frappe part dans la box avec 400 ms de retard : sans ça, un texte tapé
    // juste avant le clic passerait pour l'exemple qu'il remplace, et serait
    // écrasé.
    titleTextRef.current?.flush();
    descriptionTextRef.current?.flush();
    updateBox(box.id, (draftBox) => {
      draftBox.content.card = nextProps(draftBox.content.card || {});
      // Le texte de démonstration suit le modèle choisi : les deux textes ou
      // aucun (cf. infoCardSampleContentPatch).
      const sample = infoCardSampleContentPatch(template, {
        value: plainText(draftBox.content.titleEditorState),
        description: plainText(draftBox.content.descriptionEditorState),
      });
      if (sample.value !== undefined) {
        draftBox.content.titleEditorState = textEditorState(sample.value);
      }
      if (sample.description !== undefined) {
        draftBox.content.descriptionEditorState = textEditorState(sample.description);
      }
    });
  };

  /**
   * Enregistre la mise en forme courante comme modèle dans la banque de la
   * société, d'où elle se reposera sur n'importe quel autre document.
   *
   * Les deux textes ne partent pas (ils vivent à côté, en Draft), et la photo
   * non plus : elle appartient à la carte, pas au modèle (cf. InfoCardFields).
   */
  const handleSaveToBank = async () => {
    const name = bankStyleName.trim();
    if (!name || savingToBank) return;
    // Une opportunité/offre n'a pas de route de création propre (cf.
    // CardBankServing) : elle passe par le Proposal qui la porte, dont l'id
    // diffère du sien — même détour que la banque de formes.
    const isProposalScoped = currentDocument.type === 'opportunity' || currentDocument.type === 'offer';
    const documentId = isProposalScoped ? currentProposal?.id : currentDocument.id;
    if (!documentId) return;
    setSavingToBank(true);
    try {
      await postCardBankItem(documentId, currentDocument.type, {
        name, props: infoCardBankProps(style),
      });
      useStore.getState().bumpCardBank();
      setBankStyleName('');
      message.success(t('card-saved-to-library'));
    } catch (error) {
      message.error(t('card-save-error'));
    } finally {
      setSavingToBank(false);
    }
  };

  return (
    <>
      {/* Largeur portée par la box elle-même, comme RichEditor et Title : le
          Dragger (box non sélectionnée) n'en pose aucune sur son enveloppe. */}
      <InfoCardFace
        style={style}
        width={box.width}
        title={(
          <BoxDraftText
            ref={titleTextRef}
            box={box}
            contentKey="title"
            isSelected={isSelected}
            isActive={focusedKey === 'title'}
            textAlign={textAlign}
            panelOrder={TEXT_PANEL_ORDER}
            placeholder={t('info_card_title_placeholder')}
            style={{
              ...infoCardTitleTypography(style.titleStyle),
              width: '100%',
              ...(style.titleColor ? { color: style.titleColor } : {}),
            }}
            onFocusChange={setFocusedKey}
          />
        )}
        description={(
          <BoxDraftText
            ref={descriptionTextRef}
            box={box}
            contentKey="description"
            isSelected={isSelected}
            isActive={focusedKey === 'description'}
            textAlign={textAlign}
            panelOrder={TEXT_PANEL_ORDER}
            placeholder={t('info_card_description_placeholder')}
            style={{
              ...INFO_CARD_DESCRIPTION_STYLE,
              color: style.descriptionColor || '#868e96',
            }}
            onFocusChange={setFocusedKey}
          />
        )}
      />

      {isSelected && (
        <>
          <BoxFormControlsPortal title={t('info_card_template')} order={1}>
            <div style={groupLabelStyle('Configurateur')}>Configurateur</div>
            <BoxStyleTemplatePicker
              styles={templates}
              props={style}
              onApply={(template) => applyTemplate(template, () => boxPropsFromStyle(template))}
              renderPreview={renderTemplatePreview}
            />
            <div style={{ ...groupLabelStyle('Génériques'), marginTop: 12 }}>Génériques</div>
            <BoxStyleTemplatePicker
              styles={PRESET_TEMPLATES}
              props={style}
              onApply={(template) => applyTemplate(
                template,
                (current) => ({ ...current, ...boxPropsFromStyle(template) }),
              )}
              renderPreview={renderTemplatePreview}
            />
            <div style={{ ...groupLabelStyle('Bibliothèque'), marginTop: 12 }}>Bibliothèque</div>
            {/* Un modèle de la banque porte toute la mise en forme, comme un
                modèle du configurateur : il remplace le sac, il ne le complète
                pas. */}
            <BoxStyleTemplatePicker
              styles={bankTemplates}
              props={style}
              onApply={(template) => applyTemplate(template, () => boxPropsFromStyle(template))}
              renderPreview={renderTemplatePreview}
            />
            <LabelledRow label={t('save-to-library')} stacked>
              <div style={{ display: 'flex', gap: 4, width: '100%' }}>
                <Input
                  size='small'
                  value={bankStyleName}
                  onChange={(e) => setBankStyleName(e.target.value)}
                  placeholder={t('card-name-placeholder')}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <NavbarButton
                  tooltipContent={t('save-to-library')}
                  disabled={!bankStyleName.trim() || savingToBank}
                  onClick={handleSaveToBank}
                  icon={<DuotoneLightIcon name='floppy-disk' style={iconButtonStyle()} />}
                />
              </div>
            </LabelledRow>
          </BoxFormControlsPortal>
          <BoxFormControlsPortal title={t('info_card')} order={2}>
            <InfoCardFields
              style={style}
              box={box}
              setProp={setProp}
              labelOfColor={labelOfColor}
              colorOfLabel={colorOfLabel}
            />
          </BoxFormControlsPortal>
          <BoxActionsPanel />
        </>
      )}
    </>
  );
};

export default InfoCardBox;
